import { useHistory, useLocation } from "react-router-dom";
import { Arrayable, Nullable } from "../types/common";

export interface IParam {
  name: string;
  value?: Nullable<Arrayable<string | number>>;
}

export type TControlledParams = {
  [key: string]: Arrayable<string | number>;
};

export type TParamValues = {
  [key: string]: (string | number)[];
};

const getKeyValue = (query: URLSearchParams, key: string): string[] =>
  query.getAll(key).join(",").split(",");

export const useURLQueryString = (controlledParams?: TControlledParams) => {
  const history = useHistory();
  const location = useLocation();

  const source = controlledParams ?? location.search;

  // @ts-ignore
  const query = new URLSearchParams(source);
  // @ts-ignore
  const keys = [...new Set(Array.from(query.keys()))];

  const values = keys.reduce((acc, key) => {
    return { ...acc, [key]: getKeyValue(query, key) };
  }, {});

  const clearParams = (params: string[] | "all" = "all", replaceNow: boolean = true) => {
    if (params === "all") {
      keys.forEach((key: string) => query.delete(key));
    } else {
      params.forEach((param) => query.delete(param));
    }

    replaceNow && replaceHistory();
  };

  const getValues = (): TParamValues => {
    // @ts-ignore
    const keys = [...new Set(Array.from(query.keys()))];

    return keys.reduce((acc, key) => {
      return { ...acc, [key]: getKeyValue(query, key) };
    }, {});
  };

  const pushHistory = () => {
    query.sort();
    history.push({ pathname: location.pathname, search: query.toString() });
  };

  const replaceHistory = () => {
    query.sort();
    history.replace({ pathname: location.pathname, search: query.toString() });
  };

  const setParams = (params: IParam[]) => {
    params.forEach(({ name, value }) => {
      query.delete(name);

      if (!value || value === "") {
        return;
      }

      if (Array.isArray(value) && value.length) {
        query.set(name, value.join(","));
        return;
      }

      query.set(name, value as string);
    });
  };

  return {
    clearParams,
    getValues,
    pushHistory,
    replaceHistory,
    setParams,
    keys,
    query,
    values,
  };
};
